import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Search, Trophy, MessageCircle, Users, Activity, Star, TrendingUp, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Breadcrumbs from './Breadcrumbs';
import { generateForumDescription } from '../utils/forumDescriptions';
import { findCategoryBySlug, createCategorySlug } from '../utils/urlHelpers';
import { formatStatDelta, statDelta } from '../utils/forumStatChange';
import { isSponsorActive } from '../utils/sponsor';
import RisingForumsBlock from './RisingForumsBlock';
import CategorySummaryPanel from './CategorySummaryPanel';
import { removeAllJsonLd, resetMetaTags } from '../utils/meta';
import { applyPageSeo, categoryCanonical, setCategoryListJsonLd } from '../utils/seo';
import CategoryTrendChart from './CategoryTrendChart';
import { buildCategorySeoParagraph, latestForumUpdateIso, aggregateCategoryForums } from '../utils/categoryAggregates';
import { withCacheBust } from '../utils/mediaUrl';
import ForumCardWrapper from './ForumCardWrapper';
import type { ForumBase } from '../types/forum';

interface Forum {
  id: string;
  forum_id: string;
  name: string;
  url: string;
  category: string;
  topics_count: number;
  posts_count: number;
  members_count: number;
  founded_year: number;
  description: string;
  logo_url: string;
  is_active: boolean;
  is_sponsored: boolean;
  sponsored_until: string | null;
  previous_month_topics_count: number;
  previous_month_posts_count: number;
  previous_month_members_count: number;
  updated_at?: string | null;
}

interface Props {
  darkMode: boolean;
}

const CategoryView: React.FC<Props> = ({ darkMode }) => {
  const { categoryName } = useParams<{ categoryName: string }>();
  const [forums, setForums] = useState<Forum[]>([]);
  const [allForums, setAllForums] = useState<Forum[]>([]);
  const [categories, setCategories] = useState<Array<{name: string}>>([]);
  const [actualCategoryName, setActualCategoryName] = useState<string>('');
  const [categoryDescription, setCategoryDescription] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<keyof Forum>('members_count');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categoryName && categories.length > 0) {
      const decodedCategoryName = decodeURIComponent(categoryName);
      const foundCategory = findCategoryBySlug(decodedCategoryName, categories);
      const finalCategoryName = foundCategory || decodedCategoryName;
      
      setActualCategoryName(finalCategoryName);
      
      applyPageSeo({
        title: `${finalCategoryName} Kategorisi Forum Listesi`,
        description: `${finalCategoryName} kategorisindeki forum siteleri, üye sayıları, konu ve ileti istatistikleri.`,
        keywords: `${finalCategoryName}, ${finalCategoryName.toLowerCase()} forumları, forum listesi, forum istatistikleri`,
        canonical: categoryCanonical(finalCategoryName),
      });

      fetchCategoryMeta(finalCategoryName);
      fetchForums(finalCategoryName);
    }
  }, [categoryName, categories]);

  useEffect(() => {
    return () => {
      resetMetaTags();
      removeAllJsonLd();
    };
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  useEffect(() => {
    if (actualCategoryName) {
      setCategoryListJsonLd(actualCategoryName, forums.length);
    }
  }, [actualCategoryName, forums.length]);

  useEffect(() => {
    if (!actualCategoryName || forums.length === 0) return;
    const agg = aggregateCategoryForums(forums);
    const desc = buildCategorySeoParagraph(
      actualCategoryName,
      agg,
      latestForumUpdateIso(forums),
      categoryDescription
    );
    applyPageSeo({
      title: `${actualCategoryName} Kategorisi Forum Listesi`,
      description: desc.slice(0, 320),
      keywords: `${actualCategoryName}, ${actualCategoryName.toLowerCase()} forumları, forum listesi, forum istatistikleri, kategori özeti`,
      canonical: categoryCanonical(actualCategoryName),
    });
  }, [actualCategoryName, forums, categoryDescription]);

  const fetchCategoryMeta = async (categoryName: string) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('description')
        .eq('name', categoryName)
        .maybeSingle();
      if (error) throw error;
      setCategoryDescription(data?.description ?? null);
    } catch (error) {
      console.error('Error fetching category meta:', error);
      setCategoryDescription(null);
    }
  };

  const fetchForums = async (categoryName: string) => {
    try {
      // Fetch all forums for global rankings
      const { data: allForumsData, error: allForumsError } = await supabase
        .from('forums')
        .select('*')
        .eq('is_active', true);

      if (allForumsError) throw allForumsError;
      setAllForums(allForumsData || []);

      // Fetch category forums
      const { data, error } = await supabase
        .from('forums')
        .select('*')
        .eq('category', categoryName)
        .eq('is_active', true);

      if (error) throw error;
      setForums(data || []);
    } catch (error) {
      console.error('Error fetching forums:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (forum: Forum): number => {
    const topicsScore = (forum.topics_count ?? 0) * 5;
    const postsScore = (forum.posts_count ?? 0) * 2;
    const membersScore = (forum.members_count ?? 0) * 10;
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year ?? new Date().getFullYear()));
  };

  const calculatePreviousScore = (forum: Forum): number => {
    const topicsScore = (forum.previous_month_topics_count ?? 0) * 5;
    const postsScore = (forum.previous_month_posts_count ?? 0) * 2;
    const membersScore = (forum.previous_month_members_count ?? 0) * 10;
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year ?? new Date().getFullYear()));
  };

  const getScoreChange = (forum: Forum): { change: number; percent: number } => {
    const currentScore = calculateScore(forum);
    const previousScore = calculatePreviousScore(forum);
    const change = currentScore - previousScore;
    const percent = previousScore > 0 ? (change / previousScore) * 100 : 0;
    return { change, percent };
  };

  const getRanking = (forum: Forum, list: Forum[], field: keyof Forum): number => {
    const sortedList = [...list].sort((a, b) => {
      if (field === 'score') {
        return calculateScore(b) - calculateScore(a);
      }
      return ((b[field] as number) ?? 0) - ((a[field] as number) ?? 0);
    });
    return sortedList.findIndex(f => f.id === forum.id) + 1;
  };

  const filteredForums = forums
    .filter(forum => forum.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // First sort by sponsorship status
      if (isSponsorActive(a) && !isSponsorActive(b)) return -1;
      if (!isSponsorActive(a) && isSponsorActive(b)) return 1;

      // Then sort by the selected criteria
      if (sortBy === 'score') {
        return calculateScore(b) - calculateScore(a);
      }
      const valueA = a[sortBy] ?? 0;
      const valueB = b[sortBy] ?? 0;
      return (valueB as number) - (valueA as number);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Breadcrumbs darkMode={darkMode} />

        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} />
          Geri Dön
        </Link>

        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 break-words">{actualCategoryName}</h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Bu kategoride {forums.length} forum bulunuyor
          </p>
          <Link
            to={`/kategori/${createCategorySlug(actualCategoryName)}/istatistikler`}
            className={`inline-block mt-2 text-sm font-medium ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
          >
            {actualCategoryName} istatistik özeti →
          </Link>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
          <RisingForumsBlock
            forums={forums}
            darkMode={darkMode}
            title={`${actualCategoryName} — en çok büyüyenler`}
            limit={5}
            className="mb-0"
          />
          <CategorySummaryPanel
            categoryName={actualCategoryName}
            categoryDescription={categoryDescription}
            forums={forums}
            darkMode={darkMode}
          />
        </div>

        <div className={`mb-8 rounded-xl border p-4 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <h2 className="text-lg font-semibold mb-3">Kategori trendi</h2>
          <CategoryTrendChart categoryName={actualCategoryName} darkMode={darkMode} />
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="relative w-full md:flex-1 md:max-w-md">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
              size={20}
            />
            <input
              type="text"
              placeholder="Forum ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                  : 'border-gray-200'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            />
          </div>

          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <button
              onClick={() => setSortBy('score')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm sm:text-base ${
                sortBy === 'score'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Trophy size={18} />
              Skor
            </button>
            <button
              onClick={() => setSortBy('topics_count')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm sm:text-base ${
                sortBy === 'topics_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <MessageCircle size={18} />
              Konu
            </button>
            <button
              onClick={() => setSortBy('posts_count')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm sm:text-base ${
                sortBy === 'posts_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Activity size={18} />
              İleti
            </button>
            <button
              onClick={() => setSortBy('members_count')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm sm:text-base ${
                sortBy === 'members_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users size={18} />
              Üye
            </button>
          </div>
        </div>

        <div className="grid gap-6">
          {filteredForums.map((forum) => {
            const scoreChange = getScoreChange(forum);
            const categoryRank = getRanking(forum, forums, sortBy);
            const globalRank = getRanking(forum, allForums, sortBy);
            
            return (
              <ForumCardWrapper
                key={forum.id}
                to={`/forum/${forum.forum_id}`}
                forumId={forum.forum_id}
                darkMode={darkMode}
                adminMeta={forum as ForumBase}
                className={`block ${
                  darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                } rounded-xl p-4 sm:p-6 shadow-sm transition-all duration-200`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {forum.logo_url && (
                    <img
                      src={withCacheBust(forum.logo_url, forum.updated_at) ?? forum.logo_url}
                      alt=""
                      className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start gap-2 mb-2">
                      <h2 className="text-lg sm:text-xl font-semibold break-words min-w-0 flex-1">{forum.name}</h2>
                      {isSponsorActive(forum) && (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium shrink-0 ${
                          darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-600'
                        }`}>
                          <Star size={14} className="fill-current" />
                          Sponsorlu
                        </span>
                      )}
                      <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm w-full sm:w-auto ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        <span>Kategori #{categoryRank}</span>
                        <span>•</span>
                        <span>Genel #{globalRank}</span>
                      </div>
                    </div>
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} text-sm mb-4`}>
                      {forum.description ||
                        generateForumDescription(
                          forum.forum_id,
                          forum.name,
                          forum.category,
                          forum.members_count,
                          forum.posts_count,
                          forum.topics_count
                        )}
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <Trophy className="text-amber-500" size={18} />
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{calculateScore(forum).toLocaleString()}</span>
                          {scoreChange.change !== 0 && (
                            <div className={`flex items-center gap-1 text-sm ${
                              scoreChange.change > 0 
                                ? 'text-green-500' 
                                : scoreChange.change < 0 
                                ? 'text-red-500' 
                                : darkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {scoreChange.change > 0 ? (
                                <ArrowUpRight size={14} />
                              ) : scoreChange.change < 0 ? (
                                <ArrowDownRight size={14} />
                              ) : (
                                <Minus size={14} />
                              )}
                              <span>{Math.abs(scoreChange.change).toLocaleString()} ({scoreChange.percent > 0 ? '+' : ''}{scoreChange.percent.toFixed(1)}%)</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageCircle className="text-purple-500" size={18} />
                        <div className="flex items-center gap-2">
                          <span>{forum.topics_count.toLocaleString()}</span>
                          {formatStatDelta(forum.topics_count, forum.previous_month_topics_count) && (
                            <span
                              className={`text-sm ${
                                (statDelta(forum.topics_count, forum.previous_month_topics_count) ?? 0) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatStatDelta(forum.topics_count, forum.previous_month_topics_count)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Activity className="text-green-500" size={18} />
                        <div className="flex items-center gap-2">
                          <span>{forum.posts_count.toLocaleString()}</span>
                          {formatStatDelta(forum.posts_count, forum.previous_month_posts_count) && (
                            <span
                              className={`text-sm ${
                                (statDelta(forum.posts_count, forum.previous_month_posts_count) ?? 0) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatStatDelta(forum.posts_count, forum.previous_month_posts_count)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="text-blue-500" size={18} />
                        <div className="flex items-center gap-2">
                          <span>{forum.members_count.toLocaleString()}</span>
                          {formatStatDelta(forum.members_count, forum.previous_month_members_count) && (
                            <span
                              className={`text-sm ${
                                (statDelta(forum.members_count, forum.previous_month_members_count) ?? 0) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatStatDelta(forum.members_count, forum.previous_month_members_count)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ForumCardWrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CategoryView;